// ── useAuth — dual-mode (legacy Supabase Auth + new cookie session) ──
//
// Two staff auth modes coexist in this provider:
//
//  1. Legacy Supabase Auth (when VITE_USE_COOKIE_AUTH !== 'true').
//     supabase.auth.signInWithPassword + profiles table lookup. The
//     v0.2.0 path. Kept alive so flag-flip rollback is one redeploy.
//
//  2. Custom cookie session (when VITE_USE_COOKIE_AUTH === 'true').
//     POST /api/auth, GET /api/auth/me. JWT in nn_session httpOnly
//     cookie, validated server-side. No Supabase Auth involvement
//     for staff.
//
// The workshop-guest path (signInAsWorkshopGuest in lib/auth.js) is
// *unaffected* by this flag — it always uses Supabase anon auth, in
// either mode. The provider tracks staffUser (admin/attorney) and
// guestUser (anon Supabase session) as TWO separate state fields so
// guest redemption in another tab can't clobber the staff session
// when the cookie path is active.
//
// Removal: PR #6 deletes the legacy branch and the flag. Until then,
// any change to either branch must be tested in both modes.

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { signIn as supabaseSignIn, signOut as supabaseSignOut } from '../lib/auth.js';

const USE_COOKIE_AUTH = import.meta.env.VITE_USE_COOKIE_AUTH === 'true';

const DEFAULT_VALUE = {
  user: null,
  role: 'guest',
  orgId: null,
  isAdmin: false,
  loading: true,
  csrfToken: null,
  signIn: () => Promise.resolve({ ok: false, message: 'Auth not initialized' }),
  signOut: () => {},
};

export const AuthContext = createContext(DEFAULT_VALUE);

export function AuthProvider({ children }) {
  // Two separate state buckets — see header comment.
  const [staffUser, setStaffUser] = useState(null);   // {id,email,name,role,isAdmin,orgId}
  const [guestUser, setGuestUser] = useState(null);   // raw Supabase user (anon)
  const [legacyProfile, setLegacyProfile] = useState(null); // legacy mode only
  const [csrfToken, setCsrfToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Legacy mode helpers ──────────────────────────────────────────────
  const loadLegacyProfile = useCallback(async (userId) => {
    if (!userId) { setLegacyProfile(null); return; }
    const { data, error } = await supabase
      .from('profiles')
      .select('id, org_id, role, full_name, email')
      .eq('id', userId)
      .maybeSingle();
    if (error) {
      console.warn('useAuth: profile lookup failed:', error.message);
      setLegacyProfile(null);
      return;
    }
    setLegacyProfile(data || null);
  }, []);

  // ── Cookie-mode helpers ──────────────────────────────────────────────
  const fetchCookieSession = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (res.status === 401) return null;
      if (!res.ok) {
        console.warn('useAuth: /api/auth/me returned', res.status);
        return null;
      }
      const body = await res.json();
      return body.user || null;
    } catch (err) {
      console.warn('useAuth: /api/auth/me fetch failed', err);
      return null;
    }
  }, []);

  // ── Mount: hydrate both paths in parallel ────────────────────────────
  useEffect(() => {
    let mounted = true;

    // Cookie-mode hydration (only if flag on).
    const cookiePromise = USE_COOKIE_AUTH
      ? fetchCookieSession().then((user) => {
          if (mounted && user) setStaffUser(user);
        })
      : Promise.resolve();

    // Supabase session hydration. Always run — needed for guest path
    // and (in legacy mode) for staff path.
    const supabasePromise = supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const u = data?.session?.user || null;
      if (!u) return;
      if (USE_COOKIE_AUTH) {
        // In cookie mode, any Supabase session present here is a
        // workshop guest (staff doesn't sign in via Supabase anymore).
        setGuestUser(u);
      } else {
        // Legacy: this might be a staff Supabase session — load profile
        // to determine role. If no profile, it's a guest.
        loadLegacyProfile(u.id);
        setGuestUser(u); // also set guestUser so workshop-guest module reads it
      }
    });

    Promise.allSettled([cookiePromise, supabasePromise]).finally(() => {
      if (mounted) setLoading(false);
    });

    // Subscribe to Supabase auth changes — only updates guestUser (and,
    // in legacy mode, the legacy profile lookup). Never touches staffUser
    // when in cookie mode.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      const u = session?.user || null;
      setGuestUser(u);
      if (!USE_COOKIE_AUTH) {
        if (u) loadLegacyProfile(u.id);
        else setLegacyProfile(null);
      }
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [fetchCookieSession, loadLegacyProfile]);

  // ── signIn — branches on flag ────────────────────────────────────────
  const signIn = useCallback(async (email, password) => {
    if (USE_COOKIE_AUTH) {
      try {
        const res = await fetch('/api/auth', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), password }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          return { ok: false, message: body.message || 'Sign-in failed.' };
        }
        if (!body.user) {
          return { ok: false, message: 'Sign-in succeeded but no user returned.' };
        }
        setStaffUser(body.user);
        if (body.csrfToken) setCsrfToken(body.csrfToken);
        return { ok: true };
      } catch (err) {
        console.warn('signIn failed:', err);
        return { ok: false, message: 'Network error. Please try again.' };
      }
    }
    // Legacy
    try {
      await supabaseSignIn(email, password);
      return { ok: true };
    } catch (err) {
      const message = err?.friendlyMessage || err?.message || 'Sign-in failed.';
      console.warn('signIn failed:', message);
      return { ok: false, message };
    }
  }, []);

  // ── signOut — branches on flag ───────────────────────────────────────
  const signOut = useCallback(async () => {
    if (USE_COOKIE_AUTH) {
      try {
        await fetch('/api/auth', { method: 'DELETE', credentials: 'include' });
      } catch (_) { /* best-effort */ }
      setStaffUser(null);
      setCsrfToken(null);
      return;
    }
    try { await supabaseSignOut(); } catch (_) { /* best-effort */ }
  }, []);

  // ── Derived value ────────────────────────────────────────────────────
  // staffUser is the source of truth for admin/attorney identity in BOTH
  // modes. In legacy mode we synthesize one from legacyProfile so the
  // public surface (user, role, isAdmin, orgId) is identical across
  // modes — switching flag values doesn't change consumer code.
  const effectiveStaff = USE_COOKIE_AUTH
    ? staffUser
    : (legacyProfile ? {
        id: legacyProfile.id,
        email: legacyProfile.email,
        name: legacyProfile.full_name,
        role: legacyProfile.role,
        isAdmin: legacyProfile.role === 'admin' || legacyProfile.role === 'attorney',
        orgId: legacyProfile.org_id,
      } : null);

  const role = effectiveStaff?.role || 'guest';
  const value = {
    // Public surface (consumed by NovoNavigator etc.). For consumers,
    // `user` is the *staff* user. The guest user is only relevant to
    // workshop modules and they read it via guestUser explicitly.
    user: effectiveStaff,
    role,
    orgId: effectiveStaff?.orgId || null,
    isAdmin: effectiveStaff?.isAdmin === true || role === 'admin' || role === 'attorney',
    loading,
    csrfToken,
    signIn,
    signOut,
    // Workshop-guest module reads this directly to know the anon user.
    guestUser,
  };

  return React.createElement(AuthContext.Provider, { value }, children);
}

export function useAuth() {
  return useContext(AuthContext);
}
